import React, { useRef } from 'react';

import type { PopperProps } from '@mui/material';
import { ListItemIcon, ListItemText, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { Calendar, Check, Hash, ListCheck, ListChecks, Type } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { useClickAway } from 'react-use';
import { IconColors } from 'src/components/icons/icon-utils';
import { Apollo } from 'src/gql/generated';
import { domElIdCreators } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

const EXTRACTION_QUESTION_TYPE_ICON_SIZE = 18;

const EXTRACTION_QUESTION_TYPE_ICONS = {
  [Apollo.ExtractionQuestionType.Boolean]: Check,
  [Apollo.ExtractionQuestionType.Date]: Calendar,
  [Apollo.ExtractionQuestionType.MultiSelect]: ListChecks,
  [Apollo.ExtractionQuestionType.Number]: Hash,
  [Apollo.ExtractionQuestionType.SingleSelect]: ListCheck,
  [Apollo.ExtractionQuestionType.Text]: Type,
} satisfies Record<Apollo.ExtractionQuestionType, React.ComponentType<LucideProps>>;

export const useTypeMenuStyles = makeStyles()(theme => ({
  listItemIcon: {
    marginRight: theme.spacing(2),
    minWidth: 'unset !important',
  },
  popper: {
    zIndex: theme.zIndex.modal,
  },
}));

interface ExtractionQuestionTypePopperProps extends ExtractionQuestionTypeMenuItemsProps {
  popperProps: Omit<PopperProps, 'children'>;
}

export function ExtractionQuestionTypePopper({
  onChangeType,
  onClose,
  popperProps: { anchorEl, placement, ...rest },
  selectedType,
}: ExtractionQuestionTypePopperProps): JSX.Element {
  const { classes } = useTypeMenuStyles();
  const popperRef = useRef<HTMLDivElement>(null);

  useClickAway(popperRef, onClose);

  return (
    <Popper
      ref={popperRef}
      anchorEl={anchorEl}
      className={classes.popper}
      id={domElIdCreators.questionTypeMenuEl()}
      placement={placement}
      onClick={e => e.stopPropagation()}
      {...rest}
    >
      <Paper>
        <MenuList>
          <ExtractionQuestionTypeMenuItems
            selectedType={selectedType}
            onChangeType={onChangeType}
            onClose={onClose}
          />
        </MenuList>
      </Paper>
    </Popper>
  );
}

interface ExtractionQuestionTypeMenuItemsProps {
  onChangeType: (type: Apollo.ExtractionQuestionType) => void;
  onClose: () => void;
  selectedType: Apollo.ExtractionQuestionType;
}

export const orderedQuestionTypes = [
  Apollo.ExtractionQuestionType.Text,
  Apollo.ExtractionQuestionType.Date,
  Apollo.ExtractionQuestionType.Number,
  Apollo.ExtractionQuestionType.Boolean,
  Apollo.ExtractionQuestionType.SingleSelect,
  Apollo.ExtractionQuestionType.MultiSelect,
];

export function ExtractionQuestionTypeMenuItems({
  onChangeType,
  onClose,
  selectedType,
}: ExtractionQuestionTypeMenuItemsProps): JSX.Element {
  const { classes } = useTypeMenuStyles();

  return (
    <>
      {orderedQuestionTypes.map(type => {
        return (
          <MenuItem
            key={String(type)}
            selected={selectedType === type}
            onClick={e => {
              e.stopPropagation();
              onClose();
              onChangeType(type);
            }}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <ExtractionQuestionTypeIcon editing type={type} />
            </ListItemIcon>
            <ListItemText>{questionTypeToLabel(type)}</ListItemText>
          </MenuItem>
        );
      })}
    </>
  );
}

export function questionTypeToLabel(type: Apollo.ExtractionQuestionType): string {
  switch (type) {
    case Apollo.ExtractionQuestionType.Number:
      return 'Number';
    case Apollo.ExtractionQuestionType.Boolean:
      return 'Boolean';
    case Apollo.ExtractionQuestionType.Date:
      return 'Date';
    case Apollo.ExtractionQuestionType.Text:
      return 'Text';
    case Apollo.ExtractionQuestionType.SingleSelect:
      return 'Single Select';
    case Apollo.ExtractionQuestionType.MultiSelect:
      return 'Multi Select';
  }
}

export function ExtractionQuestionTypeIcon({
  editing,
  type,
}: {
  editing: boolean;
  type: Apollo.ExtractionQuestionType;
}): JSX.Element {
  const Icon = EXTRACTION_QUESTION_TYPE_ICONS[type];
  const color = editing ? IconColors.DarkGrey : IconColors.Primary;

  return <Icon color={color} size={EXTRACTION_QUESTION_TYPE_ICON_SIZE} />;
}
